import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useModuli } from "@/hooks/useModuli";
import { cn } from "@/lib/utils";
import type { Modulo, ModuloStato } from "@/types/api";

const STATO_LABEL: Record<ModuloStato, string> = {
  roadmap: "Prossimamente",
  anteprima: "Anteprima",
  beta: "Beta",
  live: "Live",
  dismesso: "Dismesso",
};

const STATO_COLORE: Record<ModuloStato, string> = {
  roadmap: "bg-muted text-muted-foreground border-muted-foreground/20",
  anteprima: "bg-primary/10 text-primary border-primary/30",
  beta: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  live: "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800",
  dismesso: "bg-muted/40 text-muted-foreground/60 line-through",
};

export function WidgetAccessoModuli() {
  const { data, isLoading } = useModuli();

  if (isLoading) {
    return (
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  // Solo moduli funzionali, visibili al cliente, ordinati per `ordine`.
  const moduli = data
    .filter((m) => m.tipo === "modulo_funzionale" && m.is_visibile_cliente)
    .sort((a, b) => a.ordine - b.ordine);

  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
      {moduli.map((m) => (
        <CardModulo key={m.id} modulo={m} />
      ))}
    </div>
  );
}

function CardModulo({ modulo: m }: { modulo: Modulo }) {
  const attivo = m.stato === "live" || m.stato === "beta" || m.stato === "anteprima";
  const href = (() => {
    if (m.slug === "order-readiness") return "/moduli/order-readiness";
    if (m.slug === "spedizioni" || m.slug === "document-generation")
      return "/moduli/spedizioni";
    return `/moduli/${m.slug}`;
  })();

  const className = cn(
    "group rounded-lg border p-3 space-y-2 bg-card/40 transition-colors",
    attivo ? "hover:bg-card cursor-pointer block" : "opacity-70 cursor-default block"
  );
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-mono text-muted-foreground">
          {m.codice.toUpperCase()}
        </span>
        <Badge
          variant="outline"
          className={cn("text-[10px]", STATO_COLORE[m.stato])}
        >
          {STATO_LABEL[m.stato]}
        </Badge>
      </div>
      <p className="text-sm font-medium leading-tight">{m.nome}</p>
      {m.sottotitolo && (
        <p className="text-[11px] text-muted-foreground line-clamp-2">
          {m.sottotitolo}
        </p>
      )}
      {attivo && (
        <ArrowRight className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </>
  );

  return attivo ? (
    <Link to={href} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
